/**
 * Habitia — Tema de la App de Residente ("Radial").
 *
 * Capa de tokens AISLADA para las pantallas de residente (typeuser 0).
 * NO reemplaza `designTokens.js` (que gobierna el panel admin); coexiste con él.
 * Solo lo importan las primitivas de `src/components/app/` y las pantallas de residente.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TABLA MASTER de colores (fuente de verdad). El bloque `.residentApp` en
 * `src/index.css` es un ESPEJO de esta tabla: si cambia un hex acá, actualizarlo allá.
 * Valores extraídos de design_handoff_habitia_app_radial/"Habitia - App Radial.html".
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * REGLA TIPOGRÁFICA (obligatoria, ver DESIGN_SYSTEM.md): SOLO peso 400.
 * La app original usa 700/800 para montos y títulos; nosotros NO.
 * La jerarquía se recrea con TAMAÑO + COLOR + MAYÚSCULAS + letter-spacing + tabular-nums.
 * Todos los estilos de `appType` fijan explícitamente fontWeight:400.
 */

// ─── Paleta (light + estructura dark-ready, hoy se usa light) ─────────────────

const palette = {
  light: {
    blue: "#0080FF",
    blue2: "#0066D6",
    sky: "#2E93FF",
    navy: "#0E2340",
    deep: "#0A3A82",

    // Fondo "blanco de la plataforma": gris frío muy claro (NUNCA beige).
    page: "#F5F7FA",
    surface: "#FFFFFF",
    // Caja de iconos: gris frío claro, no beige.
    surface2: "#EAEEF4",
    // Caja de valor / acento frío. Más marcada que la página para que se distinga.
    cream: "#E6EBF3",

    ink: "#12141A",
    ink2: "#5D636E",
    ink3: "#9AA0A9",

    line: "rgba(18,20,26,.09)",
    line2: "rgba(18,20,26,.055)",

    green: "#0E9F57",
    red: "#E23B3B",
    amber: "#D98200",
    // Violeta frío: acento secundario para canales/estados combinados (p.ej. "Ambos").
    violet: "#8B5CF6",

    onBlue: "#FFFFFF",

    // Scrim de tinta fría para overlays (carga de imagen, etc.). Coherente con appShadows.
    scrim: "rgba(18,20,26,.45)",
  },
  // Estructura presente para el futuro; NO cableado a la UI todavía.
  dark: {
    blue: "#2E93FF",
    blue2: "#1A7BEA",
    sky: "#2E93FF",
    navy: "#0C1D34",
    deep: "#0B3168",

    page: "#0C0F13",
    surface: "#161B22",
    surface2: "#1C222B",
    cream: "#20252E",

    ink: "#F1F3F5",
    ink2: "#98A0AB",
    ink3: "#666E79",

    line: "rgba(255,255,255,.09)",
    line2: "rgba(255,255,255,.05)",

    green: "#0E9F57",
    red: "#E23B3B",
    amber: "#D98200",
    violet: "#8B5CF6",

    onBlue: "#FFFFFF",

    scrim: "rgba(0,0,0,.55)",
  },
};

export const appColors = palette.light;

// ─── Radios ───────────────────────────────────────────────────────────────────

export const appRadius = {
  card: 22,
  cardSm: 20,
  tile: 18,
  button: 14,
  iconBox: 12,
  pill: 999,
  sm: 12,
};

// ─── Sombras ──────────────────────────────────────────────────────────────────

export const appShadows = {
  soft: "0 1px 2px rgba(18,20,26,.04), 0 6px 18px rgba(18,20,26,.06)",
  hero: "0 1px 2px rgba(18,20,26,.04), 0 12px 34px rgba(18,20,26,.08)",
  glow: "0 10px 30px rgba(0,110,230,.35)", // solo FAB / orb de Habi
};

// ─── Espaciado ────────────────────────────────────────────────────────────────

export const appSpacing = {
  screenPad: 20,
  gridGap: 11,
  section: 22,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
};

// ─── Tipografía (SIZE-ONLY, todo peso 400) ────────────────────────────────────

const FONT_FAMILY = "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif";

export const appType = {
  fontFamily: FONT_FAMILY,
  weight: 400, // el único peso, siempre

  // Montos grandes: receta de 5 palancas para leerse "pesados" a peso 400
  // (tamaño + color ink + tabular-nums + letter-spacing negativo + $ chico).
  amount: {
    fontFamily: FONT_FAMILY,
    fontSize: 34,
    fontWeight: 400,
    letterSpacing: "-.02em",
    fontVariantNumeric: "tabular-nums",
    color: appColors.ink,
  },
  amountCurrency: {
    fontFamily: FONT_FAMILY,
    fontSize: 20,
    fontWeight: 400,
    color: appColors.ink3,
    verticalAlign: "top",
  },
  stat: {
    fontFamily: FONT_FAMILY,
    fontSize: 24,
    fontWeight: 400,
    letterSpacing: "-.01em",
    fontVariantNumeric: "tabular-nums",
  },
  screenTitle: {
    fontFamily: FONT_FAMILY,
    fontSize: 22,
    fontWeight: 400,
    letterSpacing: "-.01em",
    color: appColors.ink,
  },
  greeting: {
    fontFamily: FONT_FAMILY,
    fontSize: 20,
    fontWeight: 400,
    letterSpacing: "-.01em",
    color: appColors.ink,
  },
  cardTitle: {
    fontFamily: FONT_FAMILY,
    fontSize: 17,
    fontWeight: 400,
    letterSpacing: "-.01em",
    color: appColors.ink,
  },
  tileName: {
    fontFamily: FONT_FAMILY,
    fontSize: 15,
    fontWeight: 400,
    color: appColors.ink,
  },
  body: {
    fontFamily: FONT_FAMILY,
    fontSize: 13.5,
    fontWeight: 400,
    lineHeight: 1.45,
    color: appColors.ink,
  },
  bodySm: {
    fontFamily: FONT_FAMILY,
    fontSize: 12.5,
    fontWeight: 400,
    color: appColors.ink2,
  },
  sectionLabel: {
    fontFamily: FONT_FAMILY,
    fontSize: 11.5,
    fontWeight: 400,
    textTransform: "uppercase",
    letterSpacing: ".09em",
    color: appColors.ink3,
  },
  statLabel: {
    fontFamily: FONT_FAMILY,
    fontSize: 10.5,
    fontWeight: 400,
    textTransform: "uppercase",
    letterSpacing: ".07em",
    color: appColors.ink2,
  },
  caption: {
    fontFamily: FONT_FAMILY,
    fontSize: 11,
    fontWeight: 400,
    color: appColors.ink3,
  },
};

// ─── Tonos de estado (mapea claves de estado a colores app) ───────────────────

/**
 * Devuelve un color de la paleta app a partir de una clave de estado.
 * Usa las mismas semánticas que statusColors.js pero en la paleta cálida.
 * @param {string} status
 * @returns {string} color hex
 */
export function appStatusTone(status) {
  const s = (status || "").toString().toLowerCase();
  if (
    /(pagad|confirmad|resuelt|activ|al d[ií]a|aprobad|cerrad|baja)/.test(s)
  )
    return appColors.green;
  if (/(mora|vencid|deuda|alta|urgente|error|rechazad|anulad|cancelad)/.test(s))
    return appColors.red;
  if (/(pendiente|media|proceso|revisi[oó]n|espera|advertenc)/.test(s))
    return appColors.amber;
  return appColors.blue;
}

// ─── Export agrupado ──────────────────────────────────────────────────────────

export const appTheme = {
  colors: appColors,
  radius: appRadius,
  shadows: appShadows,
  spacing: appSpacing,
  type: appType,
};

export default appTheme;
